import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import React, { useMemo } from 'react';
import {
  formatTimeXAxisText,
  getChartPosition,
  getChartsGradientColor,
} from '@/pages/opinion/utils/chart';
import { echartsContent } from '@/pages/opinion/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import { NO_DATE } from '@/pages/opinion/constants';
import {
  eChartPercentageYAxis,
  reviewsFormat,
} from '@/pages/opinion/utils/value';
import {
  dealwithHoldData,
  dealwithTimeline,
  getYAxisMinMaxData,
} from '../../utils';
import './index.scss';
import { momentTimeAAfterB } from '@op/utils/time';
import { DBO_TM } from '@op/utils/format';

export default function ReviewsRealtime({
  filter,
  newValue,
  oldValue,
  valueKey,
  list,
  leftTitle,
  leftIcon,
  icon,
  height,
  formatValue = reviewsFormat,
  top = 25,
  yAxisFormatValue = echartsContent,
  calculationMaxMin = false,
  isRatio = false,
  filterZero = false,
  isSelected = false,
  hasArea = false,
  chartType = 'line',
  hasEmptyTitle = true,
  emptyDescText,
}: any) {
  const { formatMessage } = useIntl();

  const [options, empty] = useMemo(() => {
    const utcMinusTime = DBO_TM.utc().format('YYYY-MM-DD HH:mm:ss');
    const { oldYAxis, yAxis, xAxis, timeMapValue } = dealwithTimeline(
      newValue,
      oldValue,
      valueKey,
    );

    const series: {
      name: string;
      type: string;
      data: any[];
      itemStyle: any;
      lineStyle: any;
      endLabel: any;
    }[] = [];
    const legends: {
      name: string;
      itemStyle: { color: string };
    }[] = [];
    const flag = isSelected && chartType === 'bar';

    list.forEach(({ name, color }: any, index: number) => {
      const isCurrent = index === 0;
      if (flag && !isCurrent) return;

      legends.push({
        name: name,
        itemStyle: {
          color: color,
        },
      });

      let item: any = {
        name: name,
        type: chartType,
        data: isCurrent ? yAxis : oldYAxis,
        itemStyle: { color },
        lineStyle: {
          type: isCurrent ? 'solid' : 'dashed',
        },
        emphasis: {
          itemStyle: {
            color: color,
            borderColor: '#fff',
            borderWidth: 1,
            borderType: 'solid',
            opacity: 1,
          },
          lineStyle: {
            color: color,
          },
        },
      };

      if (hasArea) {
        item.areaStyle = getChartsGradientColor(
          color,
          0,
          isCurrent ? 0.5 : 0.4,
        );
      }
      // 当前数据并且是selected的图表，就有下面这类操作
      if (isCurrent) {
        // item.data = item.data.map((item: number, index: number) => {
        //   const currentTime = xAxis[index];
        //   if (momentTimeAAfterB(utcMinusTime, currentTime)) {
        //     return item;
        //   }
        //   return null;
        // });
        // // 如果是选中时间段的数据，那么就要做下面操作，对应”Realtime Ratings - Added Reviews“
        // if (isSelected) {
        //   // 如果最后值为0，那么就不展示该值，除非已经将当前时间段向前推两个值后还是没有数据
        //   let flagNum = 2;
        //   for (let index = item.data.length - 1; index >= 0; index--) {
        //     const element = item.data[index];
        //     if (element === null || flagNum === 0) continue;
        //     if (element === 0) item.data[index] = null;
        //     if (element > 0) break;
        //     flagNum -= 1;
        //   }
        // }

        item = dealwithHoldData(item, xAxis, isSelected ? 2 : 0);

        // 如果配置不展示0数据，就将0转为null
        if (filterZero) {
          item.data = item.data.map((item: number | any) => {
            const value = typeof item === 'number' ? item : item?.value || 0;
            return value === 0 ? null : item;
          });
        }

        // 为最后一个不为0的点添加label
        for (let index = item.data.length - 1; index >= 0; index--) {
          const element = item.data[index];
          if (element !== null) {
            item.data[index] = {
              value: element,
              label: {
                show: element !== 0,
                position: 'top',
                align: 'center',
                formatter: (params: any) => {
                  const value = params.value || 0;
                  return formatValue(value, 2, '0');
                },
              },
            };
            break;
          }
        }
      }
      series.push(item);
    });

    const allData = series
      .map((item) =>
        item.data.map((item) => {
          if (typeof item === 'number') return item;
          return item?.value;
        }),
      )
      .flat(2);

    let currentOptions = {
      tooltip: {
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any] },
        ) =>
          getChartPosition(pos, params, dom, rect, size, 50, 15, 'bottom', 3),
        appendToBody: false,
        confine: true,
        formatter: (params: any) => {
          const time = params?.[0]?.axisValue;
          let title = listTimeTitleFunc(time, filter.date_type, true);
          const otherTitle = listTimeTitleFunc(
            timeMapValue?.[time],
            filter?.date_type,
            true,
          );

          return formatChartParamsDate({
            params,
            listTitleFunc: (params) => {
              const name = params.seriesName;
              return `<span>${!icon
                  ? ''
                  : `<img src=${icon} style="width: 18px;height: 18px; vertical-align: middle;margin-right:4px" />`
                }${formatMessage({
                  id: name || NO_DATE.EMPTY,
                })}</span>`;
            },
            valueFormat: (value) => {
              return formatValue(value);
            },
            addExtraInfo: (element: any) => {
              return { name: element.seriesName };
            },
            tablesConfigFunc: (records: Array<any>) => {
              const periodElement = records.find(
                (item) => item.name === list[0].name,
              );

              const beforeElement = records.find(
                (item) => item.name === list[1].name,
              );

              const columns = [];

              if (periodElement) {
                columns.push({
                  showHeader: true,
                  records: [periodElement],
                  columns: [
                    {
                      key: 'text',
                      header: title,
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: leftTitle,
                    },
                  ],
                });
              }
              if (beforeElement) {
                columns.push({
                  showHeader: true,
                  records: [beforeElement],
                  columns: [
                    {
                      key: 'text',
                      header: otherTitle,
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: ' ',
                    },
                  ],
                });
              }
              return columns;
            },
          });
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filter?.date_type),
      ),
      grid: {
        top: top,
        left: 20,
        right: 20,
        containLabel: true,
      },
      animation: false,
      legend: {
        data: legends,
        formatter: (name: string) => {
          const newName = formatMessage({ id: name || NO_DATE.EMPTY });
          if (icon) {
            return `{positive|} ${newName}`;
          }
          return newName;
        },
        textStyle: {
          color: '#888888',
          fontSize: 12,
          height: 14,
          lineHeight: 14,
          rich: {
            positive: {
              height: 18,
              width: 18,
              paddingRight: 4,
              backgroundColor: {
                image: icon,
              },
            },
          },
        },
      },
      xAxis: {
        boundaryGap: ['20%', 0],
        data: xAxis,
        axisLabel: {
          showMaxLabel: true,
          formatter: (value: string) =>
            formatTimeXAxisText(value, filter?.date_type),
        },
      },
      yAxis: [
        {
          type: 'value',
          ...getYAxisMinMaxData(allData, calculationMaxMin, isRatio),
          axisLabel: {
            formatter: (value: any) => yAxisFormatValue(value),
          },
        },
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              return eChartPercentageYAxis(value);
            },
          },
        },
      ],
      series: series,
    };
    return [currentOptions, !allData.some((item) => item)];
  }, [formatMessage, newValue, oldValue, chartType]);

  return (
    <EChartCommon
      options={options}
      height={height}
      empty={empty}
      hasEmptyTitle={hasEmptyTitle}
      emptyDescText={emptyDescText}
      optionsProps={{ type: chartType }}
      leftTitle={
        <>
          {leftIcon && <img src={leftIcon}></img>}
          {leftTitle}
        </>
      }
    ></EChartCommon>
  );
}
